import { calculateBudget, convertArea, formatBudget } from "../lib/budgetEstimate.mjs";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ArrowRight, ArrowLeft, MessageCircle, Check, Calculator as CalcIcon, Info } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { TempWaNotice } from "../components/TempWaNotice";
import { submitInquiry } from "../lib/api";
import { COMPANY } from "../content";
import { track } from "../lib/analytics";
import { attributionSummary } from "../lib/waTracker";

const PROJECT_TYPES = [
  { key: "villa", label: "Villa" },
  { key: "apartment", label: "Apartment" },
  { key: "office", label: "Office / Commercial" },
  { key: "holiday", label: "Holiday Home" },
  { key: "other", label: "Other" },
];
const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Other"];
const SCOPES = [
  { key: "full-fitout", label: "New / shell fit-out" },
  { key: "renovation", label: "Full renovation" },
  { key: "kitchen", label: "Kitchen" },
  { key: "joinery", label: "Joinery / Wardrobes" },
  { key: "furnishing", label: "Furnishing" },
  { key: "curtains", label: "Curtains" },
];
const FINISHES = [
  { key: "essential", label: "Essential", desc: "Clean, durable, budget-conscious." },
  { key: "premium", label: "Premium", desc: "Elevated materials & detailing." },
  { key: "luxury", label: "Luxury", desc: "Bespoke, top-tier finishes." },
];
const TIMEFRAMES = ["As soon as possible", "Within 1–3 months", "In 3–6 months", "Just exploring"];

const PHONE_RE = /^[+()\-\s\d]{7,20}$/;

const Pill = ({ selected, onClick, testid, children }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={selected}
    className={`px-4 py-3 rounded-full text-xs tracking-[0.08em] border transition-all duration-200 ${
      selected ? "bg-[#D4A963] text-[#1C110C] border-[#D4A963] shadow-[0_2px_12px_rgba(212,169,99,0.35)]"
      : "bg-transparent text-[#1C110C] border-[#6E1F2C]/25 hover:border-[#D4A963] hover:text-[#6E1F2C]"}`}>
    {children}
  </button>
);

const initial = {
  projectType: "",
  emirate: "",
  size: "",
  unit: "sqft",
  scope: [],
  finish: "",
  timeframe: "",
};

export default function FitOutCalculator() {
  usePageMeta({
    title: "Dubai Fit-Out Budget Planner | Time Furniture",
    description:
      "Plan your interior fit-out budget in the UAE. Answer a few questions for an indicative planning band, then book a site visit for a detailed estimate. Established 1993.",
    canonical: "https://timefurniture.ae/fit-out-cost-calculator-dubai",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Dubai Fit-Out Budget Planner",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: "https://timefurniture.ae/fit-out-cost-calculator-dubai",
      offers: { "@type": "Offer", price: "0", priceCurrency: "AED" },
      publisher: { "@id": "https://timefurniture.ae/#organization" },
    },
  });

  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const [leadErrors, setLeadErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const startedRef = React.useRef(false);
  const TOTAL_STEPS = 6;

  const started = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      track.calculatorStart({ source: "calculator" });
    }
  };
  const update = (k, v) => {
    started();
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const toggleScope = (key) => {
    started();
    setForm((f) => ({
      ...f,
      scope: f.scope.includes(key) ? f.scope.filter((s) => s !== key) : [
        ...f.scope.filter(s => !(["renovation", "full-fitout"].includes(key) && ["renovation", "full-fitout"].includes(s))), key
      ],
    }));
    setErrors((e) => ({ ...e, scope: undefined }));
  };

  const estimate = useMemo(() => showResult ? calculateBudget(form) : null, [showResult, form]);
  const band = estimate?.label || null;
  const changeUnit = (unit) => {
    started();
    setForm(f => ({ ...f, unit, size: f.size ? convertArea(f.size, f.unit, unit) : "" }));
    setErrors(e => ({ ...e, size: undefined }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 1 && !form.projectType) e.projectType = "Select a project type.";
    if (s === 2 && !form.emirate) e.emirate = "Select an emirate / area.";
    if (s === 3) {
      const n = Number(form.size);
      if (!Number.isFinite(n) || n <= 0) e.size = "Enter an approximate size.";
    }
    if (s === 4 && form.scope.length === 0) e.scope = "Select at least one scope.";
    if (s === 5 && !form.finish) e.finish = "Choose a finish level.";
    if (s === 6 && !form.timeframe) e.timeframe = "Choose a start timeframe.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    track.calculatorStepComplete(step, { source: "calculator" });
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      setShowResult(true);
      // result computed by memo; emit once
      setTimeout(() => {
        const el = document.querySelector("[data-testid='calculator-result']");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    }
  };
  React.useEffect(() => {
    if (showResult && band) track.calculatorResult({ band, project_type: form.projectType, source: "calculator" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResult, band]);

  const projectLabel = PROJECT_TYPES.find((p) => p.key === form.projectType)?.label || "";
  const scopeLabels = form.scope.map((k) => SCOPES.find((s) => s.key === k)?.label).filter(Boolean);
  const finishLabel = FINISHES.find((f) => f.key === form.finish)?.label || "";

  const waText = useMemo(() => {
    const summary = [
      `Hi Time Furniture, I used your Fit-Out Budget Planner.`,
      `Project: ${projectLabel} · ${form.emirate}`,
      `Size: ${form.size} ${form.unit}`,
      `Scope: ${scopeLabels.join(", ")}`,
      `Finish: ${finishLabel}`,
      `Timeframe: ${form.timeframe}`,
      band ? `Budget guidance: ${band} (planning only)` : "",
      estimate?.range ? `Renovation reference: ${formatBudget(estimate.range)}; ${estimate.basis}` : "",
      estimate?.reason || "",
      `Please help me with a detailed estimate.`,
    ].filter(Boolean).join("\n");
    return encodeURIComponent(summary);
  }, [projectLabel, form.emirate, form.size, form.unit, scopeLabels, finishLabel, form.timeframe, band, estimate]);

  const serviceTag = form.scope.includes("full-fitout")
    ? "fitout"
    : form.scope.includes("renovation")
    ? "renovation"
    : form.scope.includes("curtains")
    ? "curtains"
    : undefined;

  const submitLead = async (e) => {
    e.preventDefault();
    const le = {};
    if (!lead.name.trim()) le.name = "Please enter your name.";
    if (!lead.phone.trim()) le.phone = "Mobile / WhatsApp number is required.";
    else if (!PHONE_RE.test(lead.phone.trim()) || lead.phone.replace(/\D/g, "").length < 7 || lead.phone.replace(/\D/g, "").length > 15)
      le.phone = "Please enter a valid phone number.";
    if (lead.email.trim() && !/^\S+@\S+\.\S+$/.test(lead.email.trim())) le.email = "Enter a valid email or leave blank.";
    setLeadErrors(le);
    if (Object.keys(le).length) return;

    setLoading(true);
    const digits = lead.phone.replace(/\D/g, "");
    const email = lead.email.trim() || `${digits || "web"}@web-lead.timefurniture.ae`;
    const attr = attributionSummary("fit-out-budget-planner");
    const message = [
      "Fit-Out Budget Planner enquiry",
      "",
      `Project type: ${projectLabel}`,
      `Emirate / area: ${form.emirate}`,
      `Approx. size: ${form.size} ${form.unit}`,
      `Scope: ${scopeLabels.join(", ")}`,
      `Finish level: ${finishLabel}`,
      `Preferred start: ${form.timeframe}`,
      `Budget guidance: ${band} (not a quotation)`,
      estimate?.range ? `Renovation reference: ${formatBudget(estimate.range)}; ${estimate.basis}` : "",
      estimate?.reason || "",
      attr ? "" : null,
      attr ? attr : null,
    ].filter((l) => l !== null).join("\n");

    try {
      await submitInquiry({
        name: lead.name.trim(),
        email,
        phone: lead.phone.trim(),
        service: `Fit-Out Budget Planner — ${scopeLabels.join(", ")}`,
        service_tag: serviceTag,
        location: form.emirate,
        property_size: `${form.size} ${form.unit}`,
        package_tier: band || undefined,
        source_page: "/fit-out-cost-calculator-dubai",
        message,
      });
      track.calculatorLeadSubmit({ band, project_type: form.projectType, source: "calculator" });
      navigate("/thank-you");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="calculator-page" className="bg-[#FAF5EC] min-h-screen">
      <Toaster theme="light" position="top-center" />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Fit-Out Budget Planner" }]} />

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-8 md:pt-12 pb-8">
        <p className="eyebrow mb-5 inline-flex items-center gap-2"><CalcIcon size={13} /> Budget Planner</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight text-[#1C110C] max-w-3xl">
          Dubai Fit-Out <em className="text-[#6E1F2C] not-italic">Budget Planner</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-[#7A6B5F] leading-relaxed">
          Plan a full villa renovation using our area-based budget guide. For other property types or individual services, share your requirements for an itemised estimate. No obligation.
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pb-24">
        <div className="bg-white border border-[#6E1F2C]/15 p-6 sm:p-8 md:p-10">
          {/* progress */}
          <div className="flex items-center gap-2 mb-8" data-testid="calculator-progress">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span key={i} className={`h-1 flex-1 rounded-full ${i < step ? "bg-[#6E1F2C]" : "bg-[#6E1F2C]/15"}`} />
            ))}
          </div>

          {!showResult && (
            <div data-testid={`calculator-step-${step}`} className="space-y-6">
              {step === 1 && (
                <fieldset>
                  <legend className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-4">1 · Project type *</legend>
                  <div className="flex flex-wrap gap-2.5">
                    {PROJECT_TYPES.map((p) => (
                      <Pill key={p.key} selected={form.projectType === p.key} testid={`calc-type-${p.key}`}
                        onClick={() => update("projectType", p.key)}>{p.label}</Pill>
                    ))}
                  </div>
                  {errors.projectType && <p className="mt-3 text-xs text-[#A3141A]" data-testid="calc-error-projectType">{errors.projectType}</p>}
                </fieldset>
              )}
              {step === 2 && (
                <div>
                  <label htmlFor="calc-emirate" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">2 · Emirate / area *</label>
                  <select id="calc-emirate" data-testid="calc-emirate" value={form.emirate}
                    onChange={(e) => update("emirate", e.target.value)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-sm focus-gold">
                    <option value="">Select…</option>
                    {EMIRATES.map((em) => <option key={em} value={em}>{em}</option>)}
                  </select>
                  {errors.emirate && <p className="mt-3 text-xs text-[#A3141A]" data-testid="calc-error-emirate">{errors.emirate}</p>}
                </div>
              )}
              {step === 3 && (
                <div>
                  <label htmlFor="calc-size" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">3 · Internal built-up area *</label>
                  <div className="flex gap-3">
                    <input id="calc-size" type="number" inputMode="decimal" min="1" data-testid="calc-size" value={form.size}
                      onChange={(e) => update("size", e.target.value)} placeholder={form.unit === "sqft" ? "e.g. 2000" : "e.g. 186"}
                      className="min-w-0 w-full flex-1 bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-sm focus-gold" />
                    <div className="flex rounded overflow-hidden border border-[#6E1F2C]/25">
                      {["sqm", "sqft"].map((u) => (
                        <button key={u} type="button" data-testid={`calc-unit-${u}`} onClick={() => changeUnit(u)} aria-pressed={form.unit === u}
                          className={`px-4 text-xs tracking-[0.12em] uppercase ${form.unit === u ? "bg-[#6E1F2C] text-[#FAF5EC]" : "bg-transparent text-[#6E1F2C]"}`}>{u}</button>
                      ))}
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-[#7A6B5F]">Use the internal floor area, not the plot or garden size. Changing units converts your measurement.</p>
                  {errors.size && <p className="mt-3 text-xs text-[#A3141A]" data-testid="calc-error-size">{errors.size}</p>}
                </div>
              )}
              {step === 4 && (
                <fieldset>
                  <legend className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-4">4 · Scope (select all that apply) *</legend>
                  <div className="flex flex-wrap gap-2.5">
                    {SCOPES.map((s) => (
                      <Pill key={s.key} selected={form.scope.includes(s.key)} testid={`calc-scope-${s.key}`}
                        onClick={() => toggleScope(s.key)}>{s.label}</Pill>
                    ))}
                  </div>
                  <p className="mt-3 text-xs text-[#7A6B5F] leading-relaxed">Full renovation means the complete interior, including kitchen and fixed joinery within the agreed scope. Individual rooms, furnishing and curtains need their own quantities and specification.</p>
                  {errors.scope && <p className="mt-3 text-xs text-[#A3141A]" data-testid="calc-error-scope">{errors.scope}</p>}
                </fieldset>
              )}
              {step === 5 && (
                <fieldset>
                  <legend className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-4">5 · Finish level *</legend>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {FINISHES.map((f) => (
                      <button key={f.key} type="button" data-testid={`calc-finish-${f.key}`} aria-pressed={form.finish === f.key} onClick={() => update("finish", f.key)}
                        className={`text-left p-4 border transition-all ${form.finish === f.key ? "border-[#D4A963] bg-[#D4A963]/10" : "border-[#6E1F2C]/20 hover:border-[#D4A963]"}`}>
                        <span className="font-serif-display text-lg text-[#1C110C]">{f.label}</span>
                        <span className="block mt-1 text-xs text-[#7A6B5F]">{f.desc}</span>
                      </button>
                    ))}
                  </div>
                  {errors.finish && <p className="mt-3 text-xs text-[#A3141A]" data-testid="calc-error-finish">{errors.finish}</p>}
                </fieldset>
              )}
              {step === 6 && (
                <div>
                  <label htmlFor="calc-timeframe" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">6 · Preferred start *</label>
                  <select id="calc-timeframe" data-testid="calc-timeframe" value={form.timeframe}
                    onChange={(e) => update("timeframe", e.target.value)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-sm focus-gold">
                    <option value="">Select…</option>
                    {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {errors.timeframe && <p className="mt-3 text-xs text-[#A3141A]" data-testid="calc-error-timeframe">{errors.timeframe}</p>}
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                {step > 1 && (
                  <button type="button" onClick={() => setStep(step - 1)} data-testid="calc-back"
                    className="inline-flex items-center gap-2 border border-[#6E1F2C]/30 text-[#6E1F2C] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:border-[#6E1F2C]">
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
                <button type="button" onClick={next} data-testid="calc-next"
                  className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-8 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F]">
                  {step === TOTAL_STEPS ? "See my budget guidance" : "Continue"} <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* RESULT (shown before gating) */}
          {showResult && (
            <div data-testid="calculator-result" className="space-y-8">
              <div className="text-center py-6">
                <p className="eyebrow mb-4">Your planning guidance</p>
                <p data-testid="calculator-band" className="font-serif-display text-4xl md:text-5xl text-[#6E1F2C]">{band}</p>
                <p data-testid="calculator-guidance" className="mt-4 max-w-2xl mx-auto text-sm leading-relaxed text-[#7A6B5F]">{estimate?.reason}</p>
                {estimate?.range && (
                  <div className="mt-5 text-sm text-[#6E1F2C]" data-testid="calculator-basis">
                    {estimate.status === "assessment" && <p className="font-medium mb-2">Renovation-only reference: {formatBudget(estimate.range)}</p>}
                    <p>{estimate.basis}</p>
                    <p className="mt-2 text-xs text-[#7A6B5F]">Structural alterations, landscaping, pools, loose furniture and window treatments require separate scope and pricing. Bespoke specifications may exceed the reference range.</p>
                  </div>
                )}
                <p className="mt-4 inline-flex items-center gap-2 text-xs text-[#7A6B5F] max-w-md mx-auto">
                  <Info size={13} className="text-[#D4A963] flex-shrink-0" />
                  This is planning guidance, not a quotation. Final scope, materials, site condition, VAT and authority fees are confirmed in your itemised estimate.
                </p>
                <div className="mt-4 text-xs text-[#7A6B5F]">
                  {projectLabel} · {form.emirate} · {form.size} {form.unit} · {scopeLabels.join(", ")} · {finishLabel}
                </div>
              </div>

              {/* Lead gate */}
              <form onSubmit={submitLead} data-testid="calculator-lead-form" noValidate className="border-t border-[#6E1F2C]/15 pt-8 space-y-5">
                <h2 className="font-serif-display text-2xl text-[#1C110C]">Get a detailed estimate</h2>
                <p className="text-sm text-[#7A6B5F] -mt-2">Share your contact and our team will prepare an itemised estimate and book a site visit.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="lead-name" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Name *</label>
                    <input id="lead-name" data-testid="calc-lead-name" autoComplete="name" required value={lead.name}
                      onChange={(e) => { setLead((l) => ({ ...l, name: e.target.value })); setLeadErrors((x) => ({ ...x, name: undefined })); }}
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-sm focus-gold" />
                    {leadErrors.name && <p className="mt-1 text-xs text-[#A3141A]" data-testid="calc-lead-error-name">{leadErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="lead-phone" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Mobile / WhatsApp *</label>
                    <input id="lead-phone" type="tel" inputMode="tel" autoComplete="tel" required data-testid="calc-lead-phone" value={lead.phone}
                      onChange={(e) => { setLead((l) => ({ ...l, phone: e.target.value })); setLeadErrors((x) => ({ ...x, phone: undefined })); }}
                      placeholder="+971 5X XXX XXXX"
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-sm focus-gold" />
                    {leadErrors.phone && <p className="mt-1 text-xs text-[#A3141A]" data-testid="calc-lead-error-phone">{leadErrors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="lead-email" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Email <span className="normal-case tracking-normal text-[#A89A8C]">(optional)</span></label>
                    <input id="lead-email" type="email" autoComplete="email" data-testid="calc-lead-email" value={lead.email}
                      onChange={(e) => { setLead((l) => ({ ...l, email: e.target.value })); setLeadErrors((x) => ({ ...x, email: undefined })); }}
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-sm focus-gold" />
                    {leadErrors.email && <p className="mt-1 text-xs text-[#A3141A]" data-testid="calc-lead-error-email">{leadErrors.email}</p>}
                  </div>
                </div>
                <p className="text-xs text-[#7A6B5F] leading-relaxed" data-testid="calc-consent">
                  By submitting, you agree that Time Furniture may contact you about this enquiry. We use your details only to respond to and manage your request.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button type="submit" disabled={loading} data-testid="calc-lead-submit"
                    className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-9 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-60">
                    {loading ? "Sending…" : "Get a detailed estimate"} <Check size={15} />
                  </button>
                  <a href={`https://wa.me/${COMPANY.whatsapp}?text=${waText}`} target="_blank" rel="noreferrer"
                    data-testid="calc-whatsapp" onClick={() => track.calculatorWhatsappClick({ band, source: "calculator" })}
                    className="inline-flex items-center gap-2 border border-[#6E1F2C]/40 text-[#6E1F2C] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-white">
                    <MessageCircle size={15} /> WhatsApp us
                  </a>
                </div>
                <TempWaNotice className="pt-1" />
                <button type="button" onClick={() => { setShowResult(false); setStep(1); setForm(initial); setErrors({}); }} data-testid="calc-restart"
                  className="text-[10px] tracking-[0.24em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C] underline underline-offset-4">
                  Start over
                </button>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
